// ============================================================================
// GrilleSalarialePage — grille salariale CE / admin (L06, F-656..660)
// ============================================================================
// SHELL de la page (GC-B) : PageHeader (annee), tabs URL-reflected (?tab= +
// alias directeurs->cadres, P-CE5), YearPicker (?year=), bandeaux ?ok=/?error=,
// badge rouge submittedCount sur l'onglet Approbations, boutons export (.xlsx +
// PDF, download direct fetch Bearer + blob, STOP-Q3). Les 4 tabs sont COMPLETS :
// GRILLE (SalaryMasterGrid, GC-B), APPROBATIONS + CADRES (GC-D), GRAPHIQUES
// (PayrollCharts, GC-E). Les liens edit (GC-C) et reel (GC-E, masse salariale
// reelle) sont DEMASQUES — leurs routes existent (plus aucun placeholder ni lien
// mort, lecon POC-DEAD-LINK).
//
// Portage du PoC (dashboard)/grille-salariale/page.tsx (SSR searchParams) au
// standard maison (SPA + useSearchParams). Gabarit : PlanificationPage (SL-A :
// garde client Forbidden, bandeaux, skeleton/QueryError) + MonEquipePage (export
// PDF fetch Bearer + blob, SL-E). Garde M-670 (P-CE14) : la garde REELLE est le
// 403 SERVEUR (admin SEUL ; director/manager/employee refuses). Le SPA ne DECIDE
// pas — il redirige le 403 vers /error/403, EN PLUS de la garde serveur (GA-05).

import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, SkeletonCard, QueryError, GsSegmented, GS_COMPONENTS } from '@gs/ui';
import { Table2, Download, FileText, Pencil, Coins, TriangleAlert } from 'lucide-react';
import { ApiForbiddenError, getRegisteredAccessToken } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useGrilleCe, useGrilleCeApprobations, useGrilleCeCadres, useGrilleCeGraphiques,
  useDecideScenario, decideErrorCode,
} from '../../hooks/useGrilleCe';
import { adaptGridClasses } from '../../hooks/usePlanification';
import { buildEmployeeLines } from '../../lib/salary/positions';
import { toLibPast, toLibHistory } from '../../lib/salary/dto-adapters';
import { aggregateByTeam, buildServiceBreakdowns } from '../../lib/salary/payroll-by-team';
import { checkBudgetRevenueTarget, DEFAULT_REVENUE_TARGET } from '../../lib/salary/payroll-targets';
import { SalaryMasterGrid } from '../../components/salary/SalaryMasterGrid';
import { ScenarioReviewPanel } from '../../components/salary/ScenarioReviewPanel';
import { CadresOverview } from '../../components/salary/CadresOverview';
import { PayrollCharts } from '../../components/salary/PayrollCharts';
import { YearPicker } from '../../components/salary/YearPicker';

const TAB_KEYS = ['grille', 'approbations', 'graphiques', 'cadres'] as const;
type GrilleTab = (typeof TAB_KEYS)[number];

/** Alias retrocompatible `directeurs`->`cadres` (P-CE5), fallback `grille`. */
function resolveTab(raw: string | null): GrilleTab {
  const aliased = raw === 'directeurs' ? 'cadres' : raw;
  return (TAB_KEYS as readonly string[]).includes(aliased ?? '') ? (aliased as GrilleTab) : 'grille';
}

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

export default function GrilleSalarialePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const tab = resolveTab(searchParams.get('tab'));
  const yearParam = searchParams.get('year');
  const okCode = searchParams.get('ok'); // approved / rejected (retour de decision GC-D)
  const errorCode = searchParams.get('error');

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const q = useGrilleCe(yearParam ? Number(yearParam) : null, !!user);

  // Garde CLIENT M-670 : le 403 serveur (non-admin) -> ecran d'erreur standard. La
  // redirection ne REMPLACE pas la garde serveur, elle la double (GA-05).
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const data = q.data;
  const selectedYear = data?.year ?? new Date().getFullYear();
  const currentYear = data?.currentYear ?? selectedYear;
  // Annees selectionnables : figees ∪ courante (le serveur a deja resolu l'annee).
  const selectable = data
    ? [...new Set([...data.gridYears, data.currentYear])].sort((a, b) => b - a)
    : [];
  const submittedCount = data?.submittedCount ?? 0;
  const hasGrid = (data?.classes.length ?? 0) > 0;

  // YearPicker : masque sur l'onglet approbations (annee courante forcee, P-CE17) ;
  // visible ailleurs ssi >1 annee selectionnable.
  const yearPicker =
    tab !== 'approbations' && selectable.length > 1 ? (
      <YearPicker years={selectable} value={selectedYear} />
    ) : null;

  // Liens d'onglets en preservant l'annee (le YearPicker preserve de son cote le tab).
  const tabHref = (value: GrilleTab) => {
    const p = new URLSearchParams();
    if (value !== 'grille') p.set('tab', value);
    if (selectedYear !== currentYear) p.set('year', String(selectedYear));
    const qs = p.toString();
    return qs ? `/grille-salariale?${qs}` : '/grille-salariale';
  };

  // Export .xlsx (MASTER, pas d'annee — quirk P-CE17) et PDF (year-aware) : download
  // direct (STOP-Q3) — fetch Bearer + blob (SL-E), jamais un <a href> nu (le Bearer
  // GS ne voyage pas dans une navigation). Chemin RELATIF uniquement.
  async function downloadExport(kind: 'xlsx' | 'pdf') {
    const getToken = getRegisteredAccessToken();
    if (!getToken) {
      setExportError(kind === 'xlsx' ? 'exportXlsx' : 'exportPdf');
      return;
    }
    setExporting(true);
    setExportError(null);
    try {
      const token = await getToken();
      const path =
        kind === 'xlsx'
          ? '/api/grille-salariale/export'
          : `/api/grille-salariale/export-pdf${selectedYear !== currentYear ? `?year=${selectedYear}` : ''}`;
      const res = await fetch(`${BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download =
        kind === 'xlsx'
          ? 'grille-salariale-2026.xlsx'
          : `grille-salariale-generale-${selectedYear}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setExportError(kind === 'xlsx' ? 'exportXlsx' : 'exportPdf');
    } finally {
      setExporting(false);
    }
  }

  // Contenu du tab GRILLE : lignes employes GLOBALES pivotees client. `currentYear`
  // (annee reelle du bundle) est le PIVOT (passe = annees < currentYear) — VERBATIM
  // PoC page.tsx:158-164 (NON selectedYear) ; SalaryMasterGrid affiche `selectedYear`.
  const grilleContent = (() => {
    if (!data) return null;
    if (!hasGrid) {
      // EmptyState SANS lien editeur (GC-C non livre — anti liens morts).
      return (
        <EmptyState
          icon={<Table2 size={48} aria-hidden="true" />}
          title={t('gest:grilleCe.empty.title')}
          description={t('gest:grilleCe.empty.description')}
        />
      );
    }
    const linesByClass = buildEmployeeLines({
      members: data.members,
      pastPositions: toLibPast(data.pastByEmail),
      echelonHistory: toLibHistory(data.historyByEmail),
      scenarioChanges: [],
      currentYear: data.currentYear,
    });
    return (
      <SalaryMasterGrid
        classes={adaptGridClasses(data.classes)}
        linesByClass={linesByClass}
        year={selectedYear}
        shadeInactive={data.shadeInactive}
        headerActions={yearPicker}
      />
    );
  })();

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:grilleCe.title', { year: selectedYear })}
        description={t('gest:grilleCe.pageDescription')}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Exports : uniquement si une grille existe (rien a exporter sinon). */}
            {hasGrid ? (
              <>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => void downloadExport('xlsx')}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
                >
                  <Download size={16} aria-hidden="true" />
                  {t('gest:grilleCe.exportXlsx')}
                </button>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => void downloadExport('pdf')}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
                >
                  <FileText size={16} aria-hidden="true" />
                  {t('gest:grilleCe.exportPdf')}
                </button>
              </>
            ) : null}
            {/* Masse salariale reelle (GC-E, demasque) : visible ssi une grille existe
                (le tableau reel s'appuie sur la grille de l'annee). Route gardee M-670. */}
            {hasGrid ? (
              <Link
                to="/grille-salariale/reel"
                className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2`}
              >
                <Coins size={16} aria-hidden="true" />
                {t('gest:grilleCe.reelLink')}
              </Link>
            ) : null}
            {/* Editer (GC-C, demasque) : toujours visible (page ADMIN — construire la
                grille meme vide passe par l'editeur). Route gardee M-670 serveur. */}
            <Link
              to="/grille-salariale/edit"
              className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-2`}
            >
              <Pencil size={16} aria-hidden="true" />
              {t('gest:grilleCe.editLink')}
            </Link>
          </div>
        }
      />

      {/* Tabs URL-reflected (P-CE5) — badge rouge submittedCount sur Approbations
          (info REELLE meme si l'onglet est un placeholder GC-D). Motif ProfileTabNav. */}
      <nav
        aria-label={t('gest:grilleCe.tabsLabel')}
        className="-mb-px flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700"
      >
        {TAB_KEYS.map((key) => {
          const isActive = key === tab;
          return (
            <Link
              key={key}
              to={tabHref(key)}
              aria-current={isActive ? 'page' : undefined}
              className={`inline-flex touch-manipulation items-center whitespace-nowrap border-b-2 px-3 py-2 text-field font-medium transition-colors ${
                isActive
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {t(`gest:grilleCe.tabs.${key}`)}
              {key === 'approbations' && submittedCount > 0 ? (
                <span className="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-chip font-semibold tabular-nums text-white">
                  {submittedCount}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>

      {/* Bandeau ?ok= (retour de decision GC-D, deep-linkable). */}
      {okCode ? (
        <div
          role="status"
          className="rounded-xl border-l-4 border-green-500 bg-green-50 p-4 text-field text-green-700 dark:bg-green-950 dark:text-green-300"
        >
          {t(`gest:grilleCe.ok.${okCode}`, { defaultValue: t('gest:grilleCe.ok.approved') })}
        </div>
      ) : null}

      {/* Bandeau ?error= + erreur d'export locale (GA-21 : jamais un code brut). */}
      {errorCode || exportError ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          {t(`gest:grilleCe.errors.${errorCode ?? exportError}`, {
            defaultValue: t('gest:grilleCe.errors.server'),
          })}
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:grilleCe.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        tab === 'grille' ? (
          grilleContent
        ) : (
          <section className={`${GS_COMPONENTS.card} p-5`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
                  {t(`gest:grilleCe.sections.${tab}.eyebrow`)}
                </span>
                <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
                  {t(`gest:grilleCe.sections.${tab}.title`)}
                </h2>
                <p className="text-meta text-slate-500 dark:text-slate-400">
                  {t(`gest:grilleCe.sections.${tab}.description`)}
                </p>
              </div>
              {yearPicker}
            </div>
            {tab === 'approbations' ? (
              <ApprobationsTab enabled={!!user} />
            ) : tab === 'cadres' ? (
              <CadresTab
                selectedYear={selectedYear}
                currentYear={currentYear}
                enabled={!!user}
              />
            ) : (
              <GraphiquesTab selectedYear={selectedYear} enabled={!!user} />
            )}
          </section>
        )
      ) : null}
    </div>
  );
}

// ═══ Tab APPROBATIONS (F-658 / M-678) — bundle propre + decision (GC-D) ════════
// Composant DEDIE (hooks non conditionnels) : monte le bundle /approbations
// seulement quand l'onglet est actif. La decision navigue ?ok=/?error= (P-CE16 :
// bandeaux deep-linkables rendus par la page). P-CE12/M-654 : useDecideScenario
// invalide AUSSI la planification L05 → le board director se deverrouille.

function ApprobationsTab({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = useGrilleCeApprobations(enabled);
  const decide = useDecideScenario();
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  // Navigue ?ok=/?error= sur l'onglet approbations (annee courante forcee : pas
  // de ?year=). Le bandeau de la page rend le message (jamais un code brut, GA-21).
  const settle = (key: 'ok' | 'error', value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'approbations');
    next.delete('year');
    next.delete(key === 'ok' ? 'error' : 'ok');
    next.set(key, value);
    setSearchParams(next);
  };

  const onDecide = (id: string, decision: 'approved' | 'rejected', note: string | null) => {
    setPendingId(id);
    decide.mutate(
      { id, decision, decisionNote: note },
      {
        onSuccess: () => { setPendingId(null); settle('ok', decision); },
        onError: (err) => { setPendingId(null); settle('error', decideErrorCode(err)); },
      },
    );
  };

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={t('gest:grilleCe.approbations.loadError')} onRetry={() => void q.refetch()} />;
  }

  return (
    <ScenarioReviewPanel
      submitted={q.data.submitted}
      recentDecisions={q.data.recentDecisions}
      classes={adaptGridClasses(q.data.classes)}
      currentYear={q.data.currentYear}
      gridYear={q.data.gridYear}
      realByEmail={q.data.realByEmail}
      onDecide={onDecide}
      pendingId={pendingId}
    />
  );
}

// ═══ Tab CADRES (F-660) — sous-onglets niveau (?niveau=, defaut 2) (GC-D) ══════
// Composant DEDIE : monte le bundle /cadres (annee-aware). Sous-onglet niveau
// URL-reflected (P-CE5) : ?niveau=1 (omis pour le defaut N2) preserve tab+year.

function CadresTab({
  selectedYear,
  currentYear,
  enabled,
}: {
  selectedYear: number;
  currentYear: number;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = useGrilleCeCadres(selectedYear, enabled);

  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const niveau: 1 | 2 = searchParams.get('niveau') === '1' ? 1 : 2;

  const onNiveauChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'cadres');
    if (value === '1') next.set('niveau', '1');
    else next.delete('niveau');
    if (selectedYear !== currentYear) next.set('year', String(selectedYear));
    else next.delete('year');
    setSearchParams(next);
  };

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={t('gest:grilleCe.cadres.loadError')} onRetry={() => void q.refetch()} />;
  }

  const group = niveau === 1 ? q.data.n1 : q.data.n2;
  const sansNiveau = q.data.sansNiveau;

  return (
    <div className="flex flex-col gap-4">
      <GsSegmented
        ariaLabel={t('gest:grilleCe.cadres.segmentAria')}
        value={String(niveau)}
        onChange={onNiveauChange}
        options={[
          { value: '2', label: t('gest:grilleCe.cadres.niveau2') },
          { value: '1', label: t('gest:grilleCe.cadres.niveau1') },
        ]}
        className="self-start"
      />
      {sansNiveau.length > 0 ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-xl border-l-4 border-amber-500 bg-amber-50 p-3 text-field text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
        >
          <TriangleAlert size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
          <p>
            <span className="font-semibold">{t('gest:grilleCe.cadres.sansNiveau.title')} : </span>
            {t('gest:grilleCe.cadres.sansNiveau.body', {
              names: sansNiveau.map((s) => s.name).join(', '),
            })}
          </p>
        </div>
      ) : null}
      <CadresOverview
        summaries={group.summaries}
        aggregates={group.aggregates}
        year={q.data.year}
        niveau={niveau}
      />
    </div>
  );
}

// ═══ Tab GRAPHIQUES (F-659) — masse reelle globale agregee CLIENT (GC-E) ═══════
// Composant DEDIE : monte le bundle /graphiques (annee-aware) et agrege CLIENT
// (aggregateByTeam/buildServiceBreakdowns, lib pures §a.4). La carte budget
// (checkBudgetRevenueTarget) compare la masse prevue TOUTES equipes au revenu
// BUDGETE + cible (repli DEFAULT_REVENUE_TARGET) — VERBATIM PoC page.tsx:311-325.

function GraphiquesTab({ selectedYear, enabled }: { selectedYear: number; enabled: boolean }) {
  const { t } = useTranslation();
  const q = useGrilleCeGraphiques(selectedYear, enabled);

  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={t('gest:grilleCe.graphiques.loadError')} onRetry={() => void q.refetch()} />;
  }

  const { realRows, revenueOutlook, targetsConfig } = q.data;
  const teams = aggregateByTeam(realRows);
  const services = buildServiceBreakdowns(realRows);

  // Carte « plan vs budget » : masse totale prevue (toutes equipes, boni exclu)
  // face au revenu BUDGETE + cible admin. Null sans budget saisi (VERBATIM PoC).
  const totalPayroll = teams.reduce((s, tm) => s + tm.cost, 0);
  const revenueTargetPct = targetsConfig.revenuePct ?? DEFAULT_REVENUE_TARGET;
  const budgetCheck =
    revenueOutlook.revenueBudgetYear > 0
      ? {
          ...checkBudgetRevenueTarget(totalPayroll, revenueOutlook.revenueBudgetYear, revenueTargetPct),
          revenueBudgetYear: revenueOutlook.revenueBudgetYear,
        }
      : null;

  return <PayrollCharts teams={teams} services={services} year={q.data.year} budgetCheck={budgetCheck} />;
}
